import { ChangeEvent, useRef, useState } from "react";
import { Box, IconButton, SxProps, Typography } from "@mui/material";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useTranslation } from "react-i18next";
import { CustomButton } from "src/design";
import {
  MAX_PROOF_SIZE_MB,
  PROOF_ACCEPT_ATTRIBUTE,
  formatFileSize,
  validateProofFile,
} from "src/business/payment/proofFile";
import {
  colorBorder,
  colorSurfaceHover,
  colorTextError,
  colorTextMuted,
  colorTextPrimary,
  radiusCard,
} from "src/design/tokens";

interface ProofFileInputProps {
  value: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  sx?: SxProps;
}

/**
 * The proof picker. It refuses an oversized or wrong-typed file before anything
 * is sent, but that is a courtesy: the backend sniffs the file's magic bytes
 * and remains the authority, so a refusal from it still has to be surfaced by
 * the caller.
 */
export const ProofFileInput = ({
  value,
  onChange,
  disabled = false,
  sx,
}: ProofFileInputProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const handleSelection = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    // Reset the input so re-picking the same file after a refusal still fires.
    event.target.value = "";

    if (!file) return;

    const refusal = validateProofFile(file);
    setErrorKey(refusal);
    onChange(refusal ? null : file);
  };

  const clear = () => {
    setErrorKey(null);
    onChange(null);
  };

  return (
    <Box sx={sx}>
      <Typography variant="caption" sx={{ color: colorTextMuted }}>
        {t("payments.proof.label")}
      </Typography>

      <input
        ref={inputRef}
        type="file"
        hidden
        accept={PROOF_ACCEPT_ATTRIBUTE}
        onChange={handleSelection}
      />

      {value ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 0.5,
            p: 1.5,
            border: `1px solid ${colorBorder}`,
            borderRadius: radiusCard,
            backgroundColor: colorSurfaceHover,
          }}
        >
          <DescriptionOutlinedIcon fontSize="small" sx={{ color: colorTextMuted }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: colorTextPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value.name}
            </Typography>
            <Typography variant="caption" sx={{ color: colorTextMuted }}>
              {formatFileSize(value.size)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={clear} disabled={disabled}>
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ mt: 0.5 }}>
          <CustomButton
            color="inverted"
            disabled={disabled}
            startIcon={<AttachFileOutlinedIcon fontSize="small" />}
            onClick={() => inputRef.current?.click()}
          >
            {t("payments.proof.choose")}
          </CustomButton>
        </Box>
      )}

      <Typography
        variant="caption"
        sx={{ display: "block", mt: 0.5, color: colorTextMuted }}
      >
        {t("payments.proof.accepted", { size: MAX_PROOF_SIZE_MB })}
      </Typography>

      {errorKey && (
        <Typography variant="caption" sx={{ display: "block", color: colorTextError }}>
          {t(errorKey, { size: MAX_PROOF_SIZE_MB })}
        </Typography>
      )}
    </Box>
  );
};
